'use client'

import { useQuery } from '@apollo/client/react'
import { FormRadio, FormRadioGroup, useFormContext } from '@ariakit/react'
import { ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import {
  Select,
  SelectOption,
  TypeStyleHeadlineMicro,
  screenReaderOnly,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog'
import { usePrettySizeName } from '../../_hooks/usePrettySizeName'
import { useStaticAmplitudeTracking } from '../../_hooks/useStaticTracking'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { FormError } from '../form/FormError'
import { useIsValidFormField } from '../form/useIsValidFormField'
import { SizeChart } from '../pages/product-pages/ProductPage/DetailsSection/SizesSection/SizeChart'

import {
  VariantSelectionFieldProps,
  VariantSelectionFieldValueDisplay,
  VariantSelectionFormContext,
  VariantSelectionFormGroupLabel,
  VariantSelectionFormGroupLabelPrimaryText,
  VariantSelectionFormState,
  VariantSelectionFromSoldOutMessage,
  VariantSelectionInvisibleInput,
  VariantSelectionOption,
  VariantSelectionOptionsRow,
  variantSelectionFormGroupLabelTypographyCss,
} from './VariantSelectionForm'

export const useVariantSelectionSizeField = (props?: Partial<VariantSelectionFieldProps>) => {
  const { product, variantGroup, sizeLabelOverride } = useContext(VariantSelectionFormContext)!
  const translations = useTranslation()
  const formContext = useFormContext()

  // Keep a local version of value, not just computed from context. So we can quickly rerender
  // with a checked state without waiting for a context rerender which can include a lot of other
  // components.
  const outerValue = formContext!.useValue<VariantSelectionFormState['size']>('size') ?? ''
  const [value, _setValue] = useState(outerValue)
  const pendingOuterUpdate = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const setValue = useCallback(
    (newValue: typeof outerValue) => {
      // Update inner value with high priority render.
      _setValue(newValue)

      // Deprioritize any external state changed via onChange prop. Only the latest selection is
      // forwarded, so rapid successive changes cannot leave stale writes queued behind it.
      clearTimeout(pendingOuterUpdate.current)
      pendingOuterUpdate.current = setTimeout(() => {
        formContext!.setValue('size', newValue)
      })
    },
    [formContext]
  )

  useUpdateEffect(() => {
    // Sync internal value to match the new one provided. This must not write back to the form,
    // otherwise two in-flight selections echo each other and loop endlessly.
    _setValue(outerValue)

    // This is on purpose. We trigger an on change like this instead of via the input so that if there are two
    // of these fields in one form, the on change for both fires if either is altered.
    // This allows variant selection fields in nested embroidery modals.
    props?.onChange?.(outerValue)
  }, [outerValue])

  const option = product.options.sizes?.find(sizeOption => sizeOption.size.id === value)
  const displayValue = option?.size.displayValue ?? value
  const renderSoldOutLabel = option?.outOfStock && variantGroup.availability === 'AVAILABLE'

  formContext!.useValidate(() => {
    const sizeFieldName = 'size' as const
    const value = formContext!.getValue(sizeFieldName)
    if (!value || value.length === 0) {
      formContext!.setError(sizeFieldName, translations.sizesSection.sizeNotSelectedErrorMessage)
    }
  })

  const prettyDisplayValue = usePrettySizeName({
    id: value,
    displayValue: displayValue,
  })

  const soldOutLabelPostfix = useMemo(() => {
    return renderSoldOutLabel ? (
      <>
        &nbsp;
        <VariantSelectionFromSoldOutMessage />
      </>
    ) : null
  }, [renderSoldOutLabel])

  const isValid = useIsValidFormField({ name: 'size' })

  const renderAsDisplayValue = useCallback(
    (value: string) => {
      const sizeOption = product.options.sizes?.find(sizeOption => sizeOption.size.id === value)
      const sizeDisplayText = sizeOption?.outOfStock
        ? `${sizeOption.size.displayValue} ${translations.giftCard.dropdownSoldOutOptionText}`
        : sizeOption
        ? sizeOption.size.displayValue
        : value.length > 0
        ? value
        : translations.cart.selectSize
      return <TypeStyleHeadlineMicro>{sizeDisplayText}</TypeStyleHeadlineMicro>
    },
    [
      product.options.sizes,
      translations.cart.selectSize,
      translations.giftCard.dropdownSoldOutOptionText,
    ]
  )

  return {
    labelText: prettyDisplayValue
      ? sizeLabelOverride ?? translations.quickBuy.modal.size
      : sizeLabelOverride
      ? translations.quickBuy.modal.selectSizeLabelOverride.replace(
          '{{sizeLabel}}',
          sizeLabelOverride
        )
      : translations.quickBuy.modal.selectSize,
    labelDisplayValue: prettyDisplayValue,
    displayValue,
    soldOutLabelPostfix,
    value,
    renderAsDisplayValue,
    setValue,
    isValid,
  }
}

type VariantSelectionSizeRadioGroupProps = {
  hideLabel?: boolean
  className?: string
  aboveOptionsSlot?: ReactNode
  belowOptionsSlot?: ReactNode
} & VariantSelectionFieldProps

export const VariantSelectionSizeRadioGroup = ({
  className,
  hideLabel = false,
  aboveOptionsSlot,
  belowOptionsSlot,
  ...baseProps
}: VariantSelectionSizeRadioGroupProps) => {
  const { product, variantGroup, secondaryTrackingLocation } = useContext(
    VariantSelectionFormContext
  )!
  const { labelText, labelDisplayValue, setValue, soldOutLabelPostfix, value } =
    useVariantSelectionSizeField(baseProps)
  const { trackStaticAmplitudeEvent } = useStaticAmplitudeTracking()
  const {
    data: currentSizeChartData,
    previousData: previousSizeChartData,
    loading: sizeChartLoading,
  } = useQuery(
    gql(`
      query FetchSizeChartId($handle: String!) {
        productGroupSizeChart(handle: $handle) {
          id
        }
      }
    `),
    {
      skip: !product.handle || hideLabel,
      variables: { handle: product.handle },
    }
  )
  const sizeChartData = !sizeChartLoading
    ? currentSizeChartData
    : currentSizeChartData ?? previousSizeChartData

  return (
    <div className={className}>
      <FormRadioGroup data-testid='size-picker'>
        <StyledFormGroupLabelWrapper hide={hideLabel}>
          <VariantSelectionFormGroupLabel>
            <VariantSelectionFormGroupLabelPrimaryText>
              {labelText}
              {labelDisplayValue ? `: ` : ` `}
            </VariantSelectionFormGroupLabelPrimaryText>
            {labelDisplayValue.length > 0 ? (
              <VariantSelectionFieldValueDisplay>
                {labelDisplayValue}
                {soldOutLabelPostfix}
              </VariantSelectionFieldValueDisplay>
            ) : null}
          </VariantSelectionFormGroupLabel>
          {/* A hidden label's size chart would still be a tab stop nobody can see. */}
          {!hideLabel && sizeChartData?.productGroupSizeChart.id !== 'null-size-chart' ? (
            <StyledSizeChart productHandle={product.handle} />
          ) : null}
        </StyledFormGroupLabelWrapper>
        {aboveOptionsSlot}
        <VariantSelectionOptionsRow $grid $wideMin={variantGroup.primaryCategory.key === 'SHOES'}>
          {product.options.sizes?.map(sizeOption => {
            const size = sizeOption.size
            const disabled = sizeOption.outOfStock
            const isSelected = size.id === value
            return (
              <FormRadio
                name={'size'}
                key={size.id}
                value={size.id}
                required
                checked={isSelected}
                onClick={() =>
                  trackStaticAmplitudeEvent({
                    productHandle: product.handle,
                    secondaryTrackingLocation,
                    modifiedElement: 'Size Selector',
                    modifiedValue: size.id,
                  })
                }
                render={htmlInputProps => {
                  return (
                    <label
                      htmlFor={htmlInputProps.id}
                      data-size-availability={disabled ? 'unavailable' : 'available'}
                    >
                      <VariantSelectionInvisibleInput
                        {...htmlInputProps}
                        onChange={event => {
                          if (event.target.checked) setValue(size.id)
                        }}
                        aria-label={size.displayValue}
                      />
                      <VariantSelectionOption
                        aria-hidden={true}
                        aria-label={size.displayValue}
                        isSelected={isSelected}
                        soldOut={disabled}
                        accentColor={undefined}
                        as='div'
                        refreshedColors
                        $noPadding
                      >
                        {size.displayValue}
                      </VariantSelectionOption>
                    </label>
                  )
                }}
              />
            )
          })}
        </VariantSelectionOptionsRow>
        <FormError name='size' />
      </FormRadioGroup>
      {belowOptionsSlot}
    </div>
  )
}

export const VariantSelectionSizeSelect = (baseProps: VariantSelectionFieldProps) => {
  const { product, secondaryTrackingLocation } = useContext(VariantSelectionFormContext)!
  const { trackStaticAmplitudeEvent } = useStaticAmplitudeTracking()
  const { labelText, setValue, renderAsDisplayValue, value, isValid } =
    useVariantSelectionSizeField(baseProps)

  const onChange = useCallback(
    (newValue: string) => {
      setValue(newValue)
      trackStaticAmplitudeEvent({
        productHandle: product.handle,
        secondaryTrackingLocation,
        modifiedElement: 'Size Selector',
        modifiedValue: newValue,
      })
    },
    [product.handle, secondaryTrackingLocation, setValue, trackStaticAmplitudeEvent]
  )

  return (
    <div>
      <Select
        value={value}
        required
        onChange={onChange}
        name={'size'}
        renderSelectValue={renderAsDisplayValue}
        isInvalid={!isValid}
        heading={labelText}
      >
        {product.options.sizes?.map(sizeOption => {
          const size = sizeOption.size
          return (
            <SelectOption key={size.id} value={size.id}>
              {renderAsDisplayValue(size.id)}
            </SelectOption>
          )
        })}
      </Select>
      <FormError name='size' />
    </div>
  )
}

const StyledFormGroupLabelWrapper = styled.div<{ hide: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: ${o => o.theme.spacing(2)};
  align-items: flex-start;
  margin-bottom: ${o => o.theme.rowGap.small};
  ${o => (o.hide ? screenReaderOnly : null)};
`

const StyledSizeChart = styled(SizeChart)`
  ${variantSelectionFormGroupLabelTypographyCss};
  flex-shrink: 0;
`
